/**
 * CategoryTileCard — expandable gateway tile for the Categorical Skill Tile System.
 *
 * Renders a tile that looks like a SkillCard but, instead of launching directly,
 * toggles a SkillPickerPanel open when clicked. The parent SkillsRail / SuggestedSkillsRail
 * controls which category is open via `isOpen` + `onToggle` so only one picker panel
 * is visible at a time.
 */

import { ChevronDown, TrendingUp, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import type { CategoryTile } from '@/services/api';

// ---------------------------------------------------------------------------
// Icon resolution — extend as new category icons are added
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, React.ElementType> = {
  TrendingUp,
  Zap,
};

// ---------------------------------------------------------------------------
// Category appearance maps — shared with SkillsRail
// ---------------------------------------------------------------------------

const CATEGORY_ICON_BG: Record<string, string> = {
  insight:  'bg-purple-50  text-purple-500',
  creative: 'bg-teal-50    text-brand-teal',
  seo:      'bg-emerald-50 text-emerald-600',
  cro:      'bg-rose-50    text-rose-500',
  paid_ads: 'bg-amber-50   text-amber-600',
  display:  'bg-slate-100  text-slate-500',
};

const CATEGORY_LABEL: Record<string, string> = {
  insight:  'Insight',
  creative: 'Creative',
  seo:      'SEO',
  cro:      'CRO',
  paid_ads: 'Paid',
  display:  'Display',
};

const CATEGORY_LABEL_COLOR: Record<string, string> = {
  insight:  'text-purple-500',
  creative: 'text-brand-teal',
  seo:      'text-emerald-600',
  cro:      'text-rose-500',
  paid_ads: 'text-amber-600',
  display:  'text-slate-500',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CategoryTileCardProps {
  tile: CategoryTile;
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export function CategoryTileCard({ tile, isOpen, onToggle, disabled }: CategoryTileCardProps) {
  const IconComponent = ICON_MAP[tile.icon] ?? TrendingUp;
  const catLabel = CATEGORY_LABEL[tile.category] ?? tile.category;
  const catLabelColor = CATEGORY_LABEL_COLOR[tile.category] ?? 'text-slate-500';
  const iconBg = CATEGORY_ICON_BG[tile.category] ?? 'bg-slate-100 text-slate-500';

  // Preview pills: first 2 sub-skill names (truncated) + chevron
  const previewNames = tile.sub_skills.slice(0, 2).map((s) => s.skill_name);

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={isOpen}
      className={clsx(
        'group relative flex flex-col text-left rounded-2xl border bg-white',
        'px-5 py-5 transition-all duration-200 shrink-0 w-56',
        'focus:outline-none',
        isOpen
          ? 'border-brand-teal/40 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.10)] ring-2 ring-brand-teal/25'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)]',
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
    >
      {/* Top row: icon + category label */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className={clsx('flex items-center justify-center w-7 h-7 rounded-lg shrink-0', iconBg)}>
          <IconComponent size={14} />
        </div>
        <span className={clsx('text-[10px] font-bold uppercase tracking-widest', catLabelColor)}>
          {catLabel}
        </span>
        {tile.signal_count > 0 && (
          <span className="ml-auto flex items-center gap-1 text-[9px] font-bold text-amber-500 bg-amber-50 border border-amber-100 rounded-full px-1.5 py-0.5 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
            {tile.signal_count}
          </span>
        )}
      </div>

      {/* Skill name */}
      <p className={clsx(
        'text-sm font-semibold leading-snug mb-1.5 transition-colors duration-150',
        isOpen ? 'text-brand-teal' : 'text-slate-800 group-hover:text-brand-teal',
      )}>
        {tile.skill_name}
      </p>

      {/* Badge — data-grounded signal summary */}
      {tile.badge && (
        <p className="text-[11px] text-amber-600 font-medium mb-2 truncate">
          {tile.badge}
        </p>
      )}

      {/* Description when no badge */}
      {!tile.badge && (
        <p className="text-[12px] text-slate-400 leading-relaxed line-clamp-2 flex-1 mb-2">
          {tile.description}
        </p>
      )}

      {/* Footer: sub-skill preview pills + expand chevron */}
      <div className="flex items-center gap-1 mt-auto pt-3 border-t border-slate-100">
        <div className="flex gap-1 flex-1 min-w-0">
          {previewNames.map((name) => (
            <span
              key={name}
              className="text-[10px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5 truncate max-w-[68px]"
            >
              {name}
            </span>
          ))}
          {tile.sub_skills.length > 2 && (
            <span className="text-[10px] text-slate-400 px-1">
              +{tile.sub_skills.length - 2}
            </span>
          )}
        </div>
        <ChevronDown
          size={14}
          className={clsx(
            'text-slate-400 transition-transform duration-200 shrink-0',
            isOpen && 'rotate-180 text-brand-teal',
          )}
        />
      </div>
    </button>
  );
}
